if (!customElements.get('sd-brand-rail')) {
  customElements.define('sd-brand-rail', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.rail = this.querySelector('[data-brand-rail]');
      this.previous = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      this.controls = this.querySelector('.sd-brand-rail__controls');
      if (!this.rail || !this.previous || !this.next) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.expand = this.querySelector('[data-rail-expand]');
      if (this.expand) {
        this.expand.hidden = false;
        this.expand.addEventListener('click', () => {
          this.stopScrolling();
          const expanded = this.expand.getAttribute('aria-expanded') !== 'true';
          if (expanded) this.railPosition = this.rail.scrollLeft;
          this.expand.setAttribute('aria-expanded', String(expanded));
          this.expand.textContent = expanded ? this.expand.dataset.expandedLabel : this.expand.dataset.collapsedLabel;
          this.rail.toggleAttribute('data-expanded', expanded);
          if (!expanded) this.rail.scrollLeft = this.railPosition || 0;
          this.update();
        }, options);
      }
      const move = direction => this.rail.scrollBy({
        left: direction * this.rail.clientWidth * 0.85,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
      });
      this.previous.addEventListener('click', () => move(-1), options);
      this.next.addEventListener('click', () => move(1), options);
      const supportsScrollEnd = 'onscrollend' in this.rail;
      this.rail.addEventListener('scroll', () => {
        this.rail.toggleAttribute('data-rail-scrolling', !this.rail.hasAttribute('data-expanded'));
        this.update();
        // Older browsers use a short idle period after the final scroll event.
        if (!supportsScrollEnd) {
          clearTimeout(this.scrollEndTimer);
          this.scrollEndTimer = setTimeout(() => this.stopScrolling(), 160);
        }
      }, { ...options, passive: true });
      this.rail.addEventListener('scrollend', () => this.stopScrolling(), options);
      this.observer = new ResizeObserver(() => this.update());
      this.observer.observe(this.rail);
      if ('IntersectionObserver' in window) {
        this.revealObserver = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this.rail.setAttribute('data-rail-reveal', '');
          this.revealObserver.unobserve(this.rail);
        }, { threshold: 0.08 });
        this.reveal();
      }
      this.update();
    }
    stopScrolling() {
      clearTimeout(this.scrollEndTimer);
      this.rail?.removeAttribute('data-rail-scrolling');
    }
    reveal() {
      if (!this.revealObserver) return;
      this.revealObserver.unobserve(this.rail);
      this.rail.removeAttribute('data-rail-reveal');
      this.revealObserver.observe(this.rail);
    }
    update() {
      const maximum = Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
      const position = Math.min(maximum, Math.max(0, this.rail.scrollLeft));
      const canScrollStart = position > 2;
      const canScrollEnd = position < maximum - 2;
      this.controls.hidden = maximum < 2;
      this.previous.disabled = !canScrollStart;
      this.next.disabled = !canScrollEnd;
      this.rail.toggleAttribute('data-can-scroll-start', canScrollStart);
      this.rail.toggleAttribute('data-can-scroll-end', canScrollEnd);
    }
    disconnectedCallback() {
      this.stopScrolling();
      this.controller?.abort();
      this.observer?.disconnect();
      this.revealObserver?.disconnect();
      this.controller = null;
    }
  });
}

if (!customElements.get('sd-brand-browser')) {
  customElements.define('sd-brand-browser', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.tabs = [...this.querySelectorAll('[data-browser-tab]')];
      this.panels = [...this.querySelectorAll('[data-browser-panel]')];
      const tablist = this.querySelector('[data-browser-tabs]');
      if (!tablist || this.tabs.length < 2) return;
      this.tablist = tablist;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      tablist.setAttribute('role', 'tablist');
      this.tabs.forEach((tab, index) => {
        tab.setAttribute('role', 'tab');
        const panel = this.panels.find(item => item.id === tab.getAttribute('aria-controls'));
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        tab.addEventListener('click', () => this.activate(index), options);
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.activate(next);
          this.tabs[next].focus();
        }, options);
      });
      this.addEventListener('shopify:block:select', event => {
        const panel = event.target.closest('[data-browser-panel]');
        const index = this.tabs.findIndex(tab => tab.getAttribute('aria-controls') === panel?.id);
        if (index >= 0) this.activate(index);
      }, options);
      this.activate(0);
      tablist.hidden = false;
      this.updateIndicator();
      this.tabObserver = new ResizeObserver(() => this.updateIndicator());
      this.tabs.forEach(tab => this.tabObserver.observe(tab, { box: 'border-box' }));
    }
    updateIndicator() {
      const activeTab = this.tabs.find(tab => tab.getAttribute('aria-selected') === 'true');
      if (!activeTab || !activeTab.offsetWidth) return;
      this.tablist.style.setProperty('--sd-tab-x', activeTab.offsetLeft + 'px');
      this.tablist.style.setProperty('--sd-tab-width', activeTab.offsetWidth + 'px');
      this.tablist.setAttribute('data-indicator-ready', '');
    }
    activate(index) {
      this.tabs.forEach((tab, position) => {
        const active = position === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        const panel = this.panels.find(item => item.id === tab.getAttribute('aria-controls'));
        const wasHidden = panel.hidden;
        panel.hidden = !active;
        if (active) {
          const rail = panel.querySelector('sd-brand-rail');
          rail?.update();
          if (wasHidden) rail?.reveal();
        }
      });
      this.querySelector('[data-browser-heading]').textContent = this.tabs[index].dataset.heading;
      this.updateIndicator();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.tabObserver?.disconnect();
      this.tablist?.removeAttribute('data-indicator-ready');
      this.controller = null;
    }
  });
}
